import { useState } from "react"
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ButtonBase, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { StandardOrderTypeFilter } from "../../../../../shared/components/filters/orderTypeFilter/StandardOrderTypeFilter"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../localization/useDateTime"

import { buildTimelineSegments, countOccupancies, formatTimeWindow } from "../meetingRoomsLib"
import { MeetingRoomBookingState, MeetingRoomTimelineMode } from "../meetingRoomsTypes"
import type { MeetingRoomBookingItem, MeetingRoomBusyInterval } from "../meetingRoomsTypes"
import { LIVE_BLINK } from "./helpers"
import { MeetingRoomTimelineBookingMeta } from "./MeetingRoomTimelineBookingMeta"

const useStyles = makeStyles()((theme) => ({
    card: {
        padding: "1rem 1.125rem .875rem",
        borderRadius: ".875rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    header: {
        flexWrap: "wrap",
    },
    modeFilter: {
        width: "auto",
    },
    hint: {
        marginTop: ".5rem",
        marginBottom: ".625rem",
    },
    segment: {
        display: "grid",
        gridTemplateColumns: ".25rem 6.5rem minmax(0, 1fr) 1.125rem",
        columnGap: ".875rem",
        alignItems: "center",
        width: "100%",
        padding: ".75rem .5rem",
        borderRadius: ".625rem",
        "&:not(:last-of-type)": {
            borderBottom: `1px solid ${theme.palette.tailwind.slate[100]}`,
        },
        textAlign: "left",
    },
    segmentBooking: {
        "&:hover": {
            backgroundColor: theme.palette.tailwind.blue[50],
        },
    },
    segmentSelected: {
        boxShadow: `inset 0 0 0 .125rem ${theme.palette.primary.main}`,
    },
    bar: {
        alignSelf: "stretch",
        borderRadius: ".25rem",
        backgroundColor: theme.palette.tailwind.emerald[100],
    },
    barInProgress: {
        backgroundColor: theme.palette.tailwind.teal[700],
        ...LIVE_BLINK,
    },
    barFinished: {
        backgroundColor: theme.palette.tailwind.slate[500],
    },
    barUpcoming: {
        backgroundColor: theme.palette.primary.main,
    },
    barOccupied: {
        backgroundColor: theme.palette.tailwind.amber[500],
    },
    range: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[900],
    },
    freeTitle: {
        fontWeight: 600,
        color: theme.palette.tailwind.teal[700],
    },
    bookingTitle: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[900],
    },
    segmentText: {
        minWidth: 0,
    },
    chevron: {
        color: theme.palette.tailwind.slate[500],
    },
}))

type Props = Readonly<{
    items: ReadonlyArray<MeetingRoomBookingItem>
    unbookedBusyIntervals: ReadonlyArray<MeetingRoomBusyInterval>
    selectedOrderId?: number
    nowMillis: number
    dayStartMillis: number
    dayEndMillis: number
    onSelect: (item: MeetingRoomBookingItem) => void
}>

export function MeetingRoomDayTimeline({
    items,
    unbookedBusyIntervals,
    selectedOrderId,
    nowMillis,
    dayStartMillis,
    dayEndMillis,
    onSelect,
}: Props) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const [mode, setMode] = useState(MeetingRoomTimelineMode.FROM_NOW)

    const isFromNow = mode === MeetingRoomTimelineMode.FROM_NOW
    const now = dateTimeFactory.fromMillis(nowMillis)
    const quarterNowMillis = now
        .set({ minute: Math.floor(now.minute / 15) * 15 })
        .startOf("minute")
        .toMillis()
    const isNowInDay = quarterNowMillis >= dayStartMillis && quarterNowMillis < dayEndMillis
    const fromMillis = isFromNow && isNowInDay ? quarterNowMillis : dayStartMillis
    const segments = buildTimelineSegments(items, unbookedBusyIntervals, fromMillis, dayEndMillis)
    const occupancyCount = countOccupancies(items, unbookedBusyIntervals)

    const barClasses: Record<MeetingRoomBookingState, string> = {
        [MeetingRoomBookingState.IN_PROGRESS]: classes.barInProgress,
        [MeetingRoomBookingState.FINISHED]: classes.barFinished,
        [MeetingRoomBookingState.UPCOMING]: classes.barUpcoming,
    }

    const modeConfig = [
        { value: MeetingRoomTimelineMode.FROM_NOW, icon: null, className: "buttonDark" as const },
        { value: MeetingRoomTimelineMode.WHOLE_DAY, icon: null, className: "buttonDark" as const },
    ]

    return (
        <Stack className={classes.card}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing=".625rem"
                useFlexGap
                className={classes.header}
            >
                <LocalizedStrict id="service-center-meeting-rooms-drawer-timeline-title">
                    <Typography variant="subtitle1">Today's schedule</Typography>
                </LocalizedStrict>
                <StandardOrderTypeFilter
                    orderType={mode}
                    setOrderType={setMode}
                    config={modeConfig}
                    size="small"
                    isMarginBottom={false}
                    isLoadingShown={false}
                    localizationBase="service-center-meeting-rooms-drawer-timeline-mode"
                    rootClassName={classes.modeFilter}
                />
            </Stack>
            <LocalizedStrict
                id={
                    isFromNow
                        ? "service-center-meeting-rooms-drawer-timeline-hint-from-now"
                        : "service-center-meeting-rooms-drawer-timeline-hint-whole-day"
                }
                vars={{ count: occupancyCount }}
            >
                <Typography variant="body2" component="div" color="text.secondary" className={classes.hint}>
                    {occupancyCount} bookings
                </Typography>
            </LocalizedStrict>
            {segments.map(({ timeRange, item, isOccupied }) =>
                item ? (
                    <ButtonBase
                        key={`booking-${item.booking.orderId}`}
                        className={cx(
                            classes.segment,
                            classes.segmentBooking,
                            item.booking.orderId === selectedOrderId && classes.segmentSelected,
                        )}
                        onClick={() => onSelect(item)}
                    >
                        <span className={cx(classes.bar, barClasses[item.state])} />
                        <Typography variant="body2" className={classes.range}>
                            {formatTimeWindow(dateTimeFactory, timeRange)}
                        </Typography>
                        <Stack className={classes.segmentText}>
                            <Typography variant="body2" noWrap className={classes.bookingTitle}>
                                {item.booking.meetingName}
                            </Typography>
                            <MeetingRoomTimelineBookingMeta booking={item.booking} />
                        </Stack>
                        <FontAwesomeIcon icon={faChevronRight} className={classes.chevron} />
                    </ButtonBase>
                ) : (
                    <div key={`free-${timeRange.startMillis}`} className={classes.segment}>
                        <span className={cx(classes.bar, isOccupied && classes.barOccupied)} />
                        <Typography variant="body2" className={classes.range}>
                            {formatTimeWindow(dateTimeFactory, timeRange)}
                        </Typography>
                        <Stack className={classes.segmentText}>
                            {isOccupied ? (
                                <LocalizedStrict id="service-center-meeting-rooms-drawer-timeline-occupied">
                                    <Typography variant="body2" className={classes.bookingTitle}>
                                        Occupied
                                    </Typography>
                                </LocalizedStrict>
                            ) : (
                                <LocalizedStrict id="service-center-meeting-rooms-drawer-timeline-free">
                                    <Typography variant="body2" className={classes.freeTitle}>
                                        Available
                                    </Typography>
                                </LocalizedStrict>
                            )}
                            <Typography variant="body2" color="text.secondary">
                                {dateTimeFactory.formatDuration(
                                    dateTimeFactory
                                        .fromMillis(timeRange.endMillis)
                                        .diff(dateTimeFactory.fromMillis(timeRange.startMillis), ["hours", "minutes"]),
                                )}
                            </Typography>
                        </Stack>
                    </div>
                ),
            )}
        </Stack>
    )
}
